import { useEffect, useState } from "react";
import { useTenant } from "@/lib/tenant-context.js";
import { Button } from "@/components/ui/button.js";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { useToast } from "@/components/ui/toast.js";
import { isoDate } from "@/lib/format.js";

/**
 * Quick "Block time" dialog — creates a one-off tenant closure for a date or
 * date range. Lighter-weight than the full ClosuresPage form (no rrule, no
 * until). The full form is still available at /closures for power users.
 */
export function BlockTimeDialog({
  open,
  onOpenChange,
  defaultDate,
  onCreated
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** YYYY-MM-DD; falls back to today. */
  defaultDate?: string;
  onCreated?: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [allDay, setAllDay] = useState(true);
  const [date, setDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [from, setFrom] = useState("09:00");
  const [to, setTo] = useState("17:00");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const start = defaultDate ?? isoDate(new Date());
    setAllDay(true);
    setDate(start);
    setEndDate(start);
    setFrom("09:00");
    setTo("17:00");
    setReason("");
  }, [open, defaultDate]);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!reason.trim()) {
      toast({ title: "Add a reason", variant: "destructive" });
      return;
    }
    if (!date) {
      toast({ title: "Pick a date", variant: "destructive" });
      return;
    }
    const startIso = allDay ? `${date}T00:00:00` : `${date}T${from}:00`;
    const endIso = allDay ? `${endDate || date}T23:59:59` : `${endDate || date}T${to}:00`;
    const startMs = Date.parse(startIso);
    const endMs = Date.parse(endIso);
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) {
      toast({ title: "End must be after start", variant: "destructive" });
      return;
    }
    const durationMinutes = Math.max(1, Math.round((endMs - startMs) / 60_000));
    setSubmitting(true);
    try {
      await api.createClosure({
        reason: reason.trim(),
        dtstart: new Date(startMs).toISOString(),
        duration_minutes: durationMinutes,
        rrule: null,
        until: null
      });
      toast({ title: "Time blocked", variant: "success" });
      onCreated?.();
      onOpenChange(false);
    } catch (error) {
      toast({ title: "Could not block", description: String(error), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Block time</DialogTitle>
        </DialogHeader>
        <form className="grid gap-3" onSubmit={submit}>
          <div className="flex flex-col gap-1">
            <Label htmlFor="block-reason">Reason</Label>
            <Input
              id="block-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Holiday, training, building closed…"
              maxLength={200}
              required
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="h-4 w-4"
            />
            <span>All day</span>
          </label>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="block-date">Start</Label>
              <Input
                id="block-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="block-end">End</Label>
              <Input
                id="block-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={date}
              />
            </div>
          </div>

          {!allDay && (
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="block-from">From</Label>
                <Input
                  id="block-from"
                  type="time"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="block-to">To</Label>
                <Input
                  id="block-to"
                  type="time"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Block"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
